import { supabase } from './client';

/**
 * Base API Service
 * Provides common API operations with error handling
 */

export class BaseAPIService {
  constructor(tableName) {
    this.tableName = tableName;
    this.supabase = supabase;
  }

  /**
   * Get all records
   */
  async getAll(options = {}) {
    try {
      let query = this.supabase.from(this.tableName).select(options.select || '*');

      // Apply filters
      if (options.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          query = query.eq(key, value);
        });
      }

      // Apply ordering
      if (options.orderBy) {
        query = query.order(options.orderBy.column, {
          ascending: options.orderBy.ascending ?? true,
        });
      }

      // Apply limit
      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error(`Error fetching from ${this.tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get single record by ID
   */
  async getById(id) {
    try {
      const { data, error } = await this.supabase
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error(`Error fetching ${this.tableName} by id:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create new record
   */
  async create(data) {
    try {
      const { data: createdData, error } = await this.supabase
        .from(this.tableName)
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: createdData };
    } catch (error) {
      console.error(`Error creating ${this.tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create multiple records
   */
  async createMany(dataArray) {
    try {
      const { data: createdData, error } = await this.supabase
        .from(this.tableName)
        .insert(dataArray)
        .select();

      if (error) throw error;
      return { success: true, data: createdData };
    } catch (error) {
      console.error(`Error creating multiple ${this.tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update record
   */
  async update(id, updates) {
    try {
      const { data, error } = await this.supabase
        .from(this.tableName)
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error(`Error updating ${this.tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete record
   */
  async delete(id) {
    try {
      const { error } = await this.supabase
        .from(this.tableName)
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error(`Error deleting ${this.tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete multiple records
   */
  async deleteMany(ids) {
    try {
      const { error } = await this.supabase
        .from(this.tableName)
        .delete()
        .in('id', ids);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error(`Error deleting multiple ${this.tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Custom query execution
   */
  async executeQuery(queryBuilder) {
    try {
      const { data, error } = await queryBuilder;
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error(`Error executing query on ${this.tableName}:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Subscribe to real-time changes
   */
  subscribe(callback, options = {}) {
    const channel = this.supabase
      .channel(`${this.tableName}_changes`)
      .on(
        'postgres_changes',
        {
          event: options.event || '*',
          schema: 'public',
          table: this.tableName,
          filter: options.filter,
        },
        callback
      )
      .subscribe();

    return channel;
  }

  /**
   * Unsubscribe from real-time changes
   */
  unsubscribe(channel) {
    this.supabase.removeChannel(channel);
  }
}